import {Navigate, useLocation} from 'react-router-dom';
import {useAuthStore} from '@common-web/stores/authStore';
import {useContext, useEffect} from 'react';
import {Spinner} from '@common-web/components/ui/spinner';
import {CODE_PERMISSIONS} from '@common/permissions';
import {useHasPermission} from '@common-web/hooks/use-permissions';
import {MS_PER_MINUTE} from '@common/constants/time';
import {PortalContext} from '@common-web/providers/portalProvider';
import {userMatchesPortal} from '@common-web/lib/portal';

function LoadingScreen() {
  return (
    <div className='flex min-h-screen items-center justify-center'>
      <Spinner className='h-8 w-8' />
    </div>
  );
}

// Portals share one session cookie, so a session from another portal can arrive
// here already live. Sign it out; callers keep the spinner up until `user`
// clears so the redirect to /login doesn't bounce off PublicGuard.
function usePortalMismatchSignOut(): boolean {
  const portal = useContext(PortalContext);
  const logout = useAuthStore((state) => state.logout);
  const user = useAuthStore((state) => state.user);
  const mismatch = !!user && !userMatchesPortal(user, portal);

  // Keyed on `user`: a get-session already in flight when we sign out can
  // re-populate the store, and that new user object must be signed out too.
  useEffect(() => {
    if (mismatch) void logout();
  }, [user, mismatch, logout]);

  return mismatch;
}

/**
 * `user.permissions` lives in the persisted auth store and only updates when
 * the session is re-fetched, so admin edits to a role's permission edges
 * don't reach an already-logged-in user until their next full page load.
 * Re-fetching on route navigation (throttled) closes that gap without a
 * per-navigation network cost. AuthGuard needs this because it wraps the
 * whole layout and stays mounted across navigations; AdminOrBrokerGuard
 * doesn't — it wraps individual routes, so its mount-time checkAuth already
 * re-fetches. The throttle timestamp is module-level and seeded at page load
 * since checkAuth covers the initial fetch.
 */
const SESSION_REFRESH_MIN_INTERVAL_MS = MS_PER_MINUTE;
let lastSessionRefreshAt = Date.now();
// One forced refresh per page load when the persisted session predates the
// permissions field — custom-role users would otherwise see a blank (static
// catalog fallback) permission set until the throttled refresh fires.
let forcedPermissionsRefresh = false;

function useSessionRefreshOnNavigate() {
  const location = useLocation();
  const refreshUser = useAuthStore((state) => state.refreshUser);
  const permissionsMissing = useAuthStore(
    (state) => !!state.user && state.user.permissions === undefined,
  );

  useEffect(() => {
    const forceRefresh = permissionsMissing && !forcedPermissionsRefresh;
    if (!forceRefresh && Date.now() - lastSessionRefreshAt < SESSION_REFRESH_MIN_INTERVAL_MS) {
      return;
    }
    if (forceRefresh) forcedPermissionsRefresh = true;
    lastSessionRefreshAt = Date.now();
    void refreshUser();
  }, [location.pathname, refreshUser, permissionsMissing]);
}

export function AuthGuard({children}: {children: React.ReactNode}) {
  const location = useLocation();
  const {user, isLoading, checkAuth} = useAuthStore();
  const portalMismatch = usePortalMismatchSignOut();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useSessionRefreshOnNavigate();

  if (isLoading || portalMismatch) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to='/login' state={{from: location}} replace />;
  }

  return <>{children}</>;
}

export function AdminOrBrokerGuard({
  children,
  // Guards the proposal-generator / RFC-submissions routes: tenant users need
  // the given permission (client admins hold the defaults); brokers pass on
  // profile type. Pass the key the route's backend endpoints actually check —
  // e.g. `page_access.view_quick_proposal` for proposal-generator routes.
  permission = CODE_PERMISSIONS.pageAccess.viewRfcQueue.key,
}: {
  children: React.ReactNode;
  permission?: string;
}) {
  const location = useLocation();
  const {user, isLoading, checkAuth} = useAuthStore();
  const hasPermission = useHasPermission(permission);
  const portalMismatch = usePortalMismatchSignOut();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (isLoading || portalMismatch) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to='/login' state={{from: location}} replace />;
  }

  const isAdmin = !!user?.clientProfile && hasPermission;
  const isBroker = !!user?.brokerProfile;

  if (!isAdmin && !isBroker) {
    return <Navigate to='/groups' replace />;
  }

  return <>{children}</>;
}

export function PublicGuard({children}: {children: React.ReactNode}) {
  const {user, isLoading, checkAuth} = useAuthStore();
  const portal = useContext(PortalContext);
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  // A mismatched session must reach the login form, not be sent into the app.
  if (user && userMatchesPortal(user, portal)) {
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
}
